import React from 'react';
import { motion } from 'framer-motion';
import { Target, Cpu, Receipt, Scale, Clock, HelpCircle } from 'lucide-react';

const agendaItems = [
  {
    icon: Target,
    title: 'Your Investment Thesis',
    description: 'Scope, timeline, and where the cost risk actually sits',
  },
  {
    icon: Cpu,
    title: 'Partner Deal Engine',
    description: 'How we configure AI systems to proactively source opportunities matching your criteria',
  },
  {
    icon: Receipt,
    title: 'Fee Structure',
    description: 'Scope, fees, and how we would run the job',
  },
  {
    icon: Scale,
    title: 'SPV Framework',
    description: 'Legal structure, ownership clarity, and regulatory simplicity',
  },
  {
    icon: Clock,
    title: 'Timeline to Deploy',
    description: 'Realistic path from call to first investment',
  },
  {
    icon: HelpCircle,
    title: 'Your Questions',
    description: "Open floor for anything we haven't covered",
  },
];

export const CallAgenda: React.FC = () => {
  return (
    <section className="py-16 sm:py-20 lg:py-24 relative bg-luxury-bg/30 dark:bg-luxury-bg-dark/30">
      <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center px-4 py-2 bg-luxury-gold/10 border border-luxury-gold/20 rounded-full mb-4"
          >
            <span className="text-sm font-medium text-luxury-gold">Call Agenda</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-luxury-text dark:text-luxury-text-dark"
          >
            What We'll{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-luxury-gold to-luxury-gold-light">
              Discuss
            </span>
          </motion.h2>
        </div>

        {/* Agenda Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {agendaItems.map((item, idx) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group bg-luxury-surface dark:bg-luxury-elevated rounded-2xl border border-luxury-gold/10 p-5 sm:p-6 hover:border-luxury-gold/30 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-luxury-gold/10 border border-luxury-gold/20 flex items-center justify-center group-hover:bg-luxury-gold/20 transition-colors duration-300">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-luxury-gold" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-luxury-text dark:text-luxury-text-dark mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-luxury-text-muted dark:text-luxury-text-muted-dark leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CallAgenda;
